/**
 * Runtime validator mirroring `catalog.ts` — the same contract, enforceable.
 *
 * Two consumers:
 * - `scripts/validate-catalog.mjs` runs `catalogSchema.parse()` on the built
 *   artifact and exits 1 on failure (the CI gate).
 * - The web app parses `catalog.json` on load, so a bad deploy fails loudly
 *   instead of rendering garbage.
 *
 * Parity with the TypeScript types is enforced at compile time by the
 * `*Parity` assertions at the bottom: if a schema drifts from its interface
 * in either direction, `tsc` fails.
 */

import { z } from "zod";
import type { Catalog, Entry } from "./catalog.ts";

/** `"YYYY-MM-DD"` */
const isoDate = z.iso.date();

/** Stable slug ids: lowercase alphanumerics and hyphens, e.g. "iron-man-2008". */
const slugId = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "expected a kebab-case slug");

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "expected #rrggbb");

/** Unit interval — weights and retention fractions. */
const fraction = z.number().min(0).max(1);

/** 0–100 scores (relevance, quality). */
const score100 = z.number().min(0).max(100);

export const tierSchema = z.enum(["essential", "important", "contextual", "optional", "skippable"]);

export const entryKindSchema = z.enum(["movie", "season", "episode", "special", "one-shot"]);

export const vocabularySchema = z.object({
  stop: z.string().min(1),
  stops: z.string().min(1),
  road: z.string().min(1),
  roster: z.string().min(1),
  rating: z.string().min(1),
});

export const featuresSchema = z.object({
  accounts: z.boolean(),
  letterboxd: z.boolean(),
  backup: z.boolean(),
  rating: z.boolean(),
  spoilers: z.boolean(),
});

export const projectInfoSchema = z.object({
  name: z.string().min(1),
  vocabulary: vocabularySchema,
  features: featuresSchema,
  copyright: z.string().min(1),
});

export const targetEventSchema = z.object({
  id: slugId,
  title: z.string().min(1),
  shortTitle: z.string().min(1),
  releaseDate: isoDate,
  externalIds: z.record(z.string(), z.string()),
});

export const milestoneSchema = z.object({
  id: slugId,
  title: z.string().min(1),
  date: isoDate,
  splitAfterEntryId: slugId.nullable(),
});

export const sagaSchema = z.object({
  id: slugId,
  title: z.string().min(1),
  order: z.number().int().min(0),
});

export const phaseSchema = z.object({
  id: slugId,
  title: z.string().min(1),
  sagaId: slugId,
  order: z.number().int().min(0),
});

export const sectionSchema = z.object({
  id: slugId,
  title: z.string().min(1),
  phaseId: slugId,
  order: z.number().int().min(0),
  blurb: z.string().min(1),
});

export const tierDefSchema = z.object({
  id: tierSchema,
  label: z.string().min(1),
  description: z.string().min(1),
  rank: z.number().int().min(1).max(5),
  solverValue: z.number().min(0),
});

export const threadSchema = z.object({
  id: slugId,
  title: z.string().min(1),
  description: z.string().min(1),
  targetWeight: fraction,
});

export const characterSchema = z.object({
  id: slugId,
  name: z.string().min(1),
  aliases: z.array(z.string().min(1)),
  targetRoster: z.boolean(),
  rosterConfidence: z.enum(["confirmed", "reported", "speculative"]),
});

export const themeTokensSchema = z.object({
  surface: hexColor,
  text: hexColor,
  textMuted: hexColor,
  accent: hexColor,
  accentOn: hexColor,
  border: hexColor,
  gradientTop: hexColor,
});

export const themeSchema = z.object({
  light: themeTokensSchema,
  dark: themeTokensSchema,
});

export const tmdbRefSchema = z.object({
  externalIds: z.record(z.string(), z.string()),
  seasonNumber: z.number().int().min(0).nullable(),
  episodeNumber: z.number().int().min(0).nullable(),
  imdbId: z
    .string()
    .regex(/^tt\d+$/)
    .nullable(),
});

export const runtimeAlternativeSchema = z.object({
  kind: z.enum(["recap", "essential-episodes", "skip-with-summary"]),
  label: z.string().min(1),
  minutes: z.number().int().positive(),
  valueRetention: fraction,
  episodeNumbers: z.array(z.number().int().positive()),
});

export const runtimeSchema = z.object({
  minutes: z.number().int().positive(),
  alternatives: z.array(runtimeAlternativeSchema),
});

export const artworkSchema = z.object({
  // A URL under `artwork: remote`, an absolute path under `local`, null under
  // `none` — so neither a leading "/" nor a URL shape can be required here.
  posterUrl: z.string().min(1).nullable(),
  backdropUrl: z.string().min(1).nullable(),
});

export const linksSchema = z.object({
  sourceUrl: z.url().nullable(),
  letterboxdSlug: slugId.nullable(),
  letterboxdUrl: z.url().nullable(),
});

export const availabilitySchema = z.object({
  disneyPlus: z.boolean(),
  letterboxdTrackable: z.boolean(),
});

export const matchKeysSchema = z.object({
  normalizedTitle: z.string().min(1),
  aliases: z.array(z.string().min(1)),
});

export const qualitySchema = z.object({
  editorial: score100.nullable(),
  editorialNote: z.string().min(1).nullable(),
  votes: z
    .object({
      average: z.number().min(0).max(10),
      count: z.number().int().min(0),
      source: z.string().min(1),
    })
    .nullable(),
  score: score100.nullable(),
});

export const characterLinkSchema = z.object({
  id: slugId,
  role: z.enum(["debut", "lead", "supporting", "cameo", "origin", "arc-defining"]),
  weight: fraction,
});

export const threadLinkSchema = z.object({
  id: slugId,
  role: z.enum(["seed", "advance", "payoff", "reference"]),
  weight: fraction,
});

export const curationSchema = z.object({
  tier: tierSchema,
  relevance: score100,
  characters: z.array(characterLinkSchema),
  threads: z.array(threadLinkSchema),
  prerequisites: z.array(slugId),
  whyItMatters: z.string().min(1).nullable(),
  whatYouMissIfSkipped: z.string().min(1),
});

export const solverFactsSchema = z.object({
  tierValue: z.number().min(0),
  unlockCount: z.number().int().min(0),
  threadMass: z.number().min(0),
  rosterMass: z.number().min(0),
  minMinutes: z.number().int().positive(),
});

/** Per-entry invariants that a plain shape check can't express. */
export const entrySchema = z
  .object({
    id: slugId,
    kind: entryKindSchema,
    title: z.string().min(1),
    // 2000, not 2008: the pre-MCU Fox X-Men films are catalog members because
    // Doomsday carries that legacy cast. X-Men (2000) is the floor.
    year: z.number().int().min(2000).max(2100),
    releaseDate: isoDate,
    orderIndex: z.number().int().min(0),
    sectionId: slugId,
    phaseId: slugId,
    sagaId: slugId,
    leg: z.union([z.literal(1), z.literal(2)]),
    source: tmdbRefSchema,
    runtime: runtimeSchema,
    artwork: artworkSchema,
    links: linksSchema,
    availability: availabilitySchema,
    match: matchKeysSchema,
    quality: qualitySchema,
    curation: curationSchema,
    solver: solverFactsSchema,
  })
  .superRefine((entry, ctx) => {
    // whyItMatters is required prose for the top two tiers.
    if (
      (entry.curation.tier === "essential" || entry.curation.tier === "important") &&
      entry.curation.whyItMatters === null
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["curation", "whyItMatters"],
        message: `whyItMatters is required for tier "${entry.curation.tier}"`,
      });
    }

    // TV is per-season: a season entry must point at a TV source + season number.
    if (entry.kind === "season") {
      if (entry.source.seasonNumber === null) {
        ctx.addIssue({
          code: "custom",
          path: ["source"],
          message: 'kind "season" requires a seasonNumber',
        });
      }
    }
    // The inverse of the season rule: a movie has no season number. Stated
    // this way round because "movie" is no longer a source-specific type — it
    // is the entry kind, and any adapter can produce one.
    if (entry.kind === "episode" && entry.source.seasonNumber === null) {
      ctx.addIssue({
        code: "custom",
        path: ["source", "seasonNumber"],
        message: 'kind "episode" requires a seasonNumber',
      });
    }

    if (entry.kind === "movie" && entry.source.seasonNumber !== null) {
      ctx.addIssue({
        code: "custom",
        path: ["source", "seasonNumber"],
        message: 'kind "movie" must not carry a seasonNumber',
      });
    }

    // An entry can't be its own prerequisite (the full DAG check is catalog-level).
    if (entry.curation.prerequisites.includes(entry.id)) {
      ctx.addIssue({
        code: "custom",
        path: ["curation", "prerequisites"],
        message: "entry lists itself as a prerequisite",
      });
    }

    // essential-episodes alternatives must actually name episodes.
    entry.runtime.alternatives.forEach((alt, i) => {
      if (alt.kind === "essential-episodes" && alt.episodeNumbers.length === 0) {
        ctx.addIssue({
          code: "custom",
          path: ["runtime", "alternatives", i, "episodeNumbers"],
          message: "essential-episodes alternative needs episodeNumbers",
        });
      }
      if (alt.minutes >= entry.runtime.minutes) {
        ctx.addIssue({
          code: "custom",
          path: ["runtime", "alternatives", i, "minutes"],
          message: "alternative is not shorter than the full runtime",
        });
      }
    });

    // Letterboxd fields must be coherent with trackability.
    if (!entry.availability.letterboxdTrackable) {
      if (entry.links.letterboxdSlug !== null || entry.links.letterboxdUrl !== null) {
        ctx.addIssue({
          code: "custom",
          path: ["links"],
          message: "letterboxd links must be null when letterboxdTrackable is false",
        });
      }
    }

    // minMinutes is the precomputed min over full runtime and alternatives.
    const expectedMin = Math.min(
      entry.runtime.minutes,
      ...entry.runtime.alternatives.map((a) => a.minutes),
    );
    if (entry.solver.minMinutes !== expectedMin) {
      ctx.addIssue({
        code: "custom",
        path: ["solver", "minMinutes"],
        message: `minMinutes ${entry.solver.minMinutes} != min(runtime, alternatives) = ${expectedMin}`,
      });
    }
  });

export const catalogTotalsSchema = z.object({
  entryCount: z.number().int().min(0),
  byKind: z.record(entryKindSchema, z.number().int().min(0)),
  byTier: z.record(tierSchema, z.number().int().min(0)),
  runtimeMinutes: z.number().int().min(0),
  minRuntimeMinutes: z.number().int().min(0),
  legRuntimeMinutes: z.object({
    leg1: z.number().int().min(0),
    leg2: z.number().int().min(0),
  }),
});

/**
 * The full artifact, including referential integrity across collections —
 * this is what `validate-catalog.mjs` runs and what the client parses on load.
 */
export const catalogSchema = z
  .object({
    schemaVersion: z.literal(2),
    catalogVersion: z.string().min(1),
    project: projectInfoSchema,
    target: targetEventSchema,
    milestones: z.array(milestoneSchema),
    sagas: z.array(sagaSchema),
    phases: z.array(phaseSchema),
    sections: z.array(sectionSchema),
    tiers: z.array(tierDefSchema),
    threads: z.array(threadSchema),
    characters: z.array(characterSchema),
    themes: z.record(z.string(), themeSchema),
    entries: z.array(entrySchema),
    totals: catalogTotalsSchema,
  })
  .superRefine((catalog, ctx) => {
    const ids = <T extends { id: string }>(xs: readonly T[]) => new Set(xs.map((x) => x.id));
    const sagaIds = ids(catalog.sagas);
    const phaseIds = ids(catalog.phases);
    const sectionIds = ids(catalog.sections);
    const threadIds = ids(catalog.threads);
    const characterIds = ids(catalog.characters);
    const tierIds = ids(catalog.tiers);
    const entryIds = new Set<string>();

    catalog.entries.forEach((entry, i) => {
      if (entryIds.has(entry.id)) {
        ctx.addIssue({
          code: "custom",
          path: ["entries", i, "id"],
          message: `duplicate entry id "${entry.id}"`,
        });
      }
      entryIds.add(entry.id);
    });

    const seenOrder = new Set<number>();
    catalog.entries.forEach((entry, i) => {
      if (seenOrder.has(entry.orderIndex)) {
        ctx.addIssue({
          code: "custom",
          path: ["entries", i, "orderIndex"],
          message: `duplicate orderIndex ${entry.orderIndex}`,
        });
      }
      seenOrder.add(entry.orderIndex);

      const refChecks: Array<[string, string, Set<string>]> = [
        ["sectionId", entry.sectionId, sectionIds],
        ["phaseId", entry.phaseId, phaseIds],
        ["sagaId", entry.sagaId, sagaIds],
      ];
      for (const [field, value, known] of refChecks) {
        if (!known.has(value)) {
          ctx.addIssue({
            code: "custom",
            path: ["entries", i, field],
            message: `unknown ${field} "${value}"`,
          });
        }
      }
      if (!tierIds.has(entry.curation.tier)) {
        ctx.addIssue({
          code: "custom",
          path: ["entries", i, "curation", "tier"],
          message: `tier "${entry.curation.tier}" has no TierDef`,
        });
      }
      entry.curation.characters.forEach((link, j) => {
        if (!characterIds.has(link.id)) {
          ctx.addIssue({
            code: "custom",
            path: ["entries", i, "curation", "characters", j, "id"],
            message: `unknown character "${link.id}"`,
          });
        }
      });
      entry.curation.threads.forEach((link, j) => {
        if (!threadIds.has(link.id)) {
          ctx.addIssue({
            code: "custom",
            path: ["entries", i, "curation", "threads", j, "id"],
            message: `unknown thread "${link.id}"`,
          });
        }
      });
    });

    // Prerequisites resolve within the catalog.
    catalog.entries.forEach((entry, i) => {
      entry.curation.prerequisites.forEach((prereq, j) => {
        if (!entryIds.has(prereq)) {
          ctx.addIssue({
            code: "custom",
            path: ["entries", i, "curation", "prerequisites", j],
            message: `unknown prerequisite "${prereq}"`,
          });
        }
      });
    });

    catalog.phases.forEach((phase, i) => {
      if (!sagaIds.has(phase.sagaId)) {
        ctx.addIssue({
          code: "custom",
          path: ["phases", i, "sagaId"],
          message: `unknown sagaId "${phase.sagaId}"`,
        });
      }
      if (!(phase.id in catalog.themes)) {
        ctx.addIssue({
          code: "custom",
          path: ["themes"],
          message: `missing theme for phase "${phase.id}"`,
        });
      }
    });

    catalog.sections.forEach((section, i) => {
      if (!phaseIds.has(section.phaseId)) {
        ctx.addIssue({
          code: "custom",
          path: ["sections", i, "phaseId"],
          message: `unknown phaseId "${section.phaseId}"`,
        });
      }
    });

    catalog.milestones.forEach((milestone, i) => {
      if (milestone.splitAfterEntryId !== null && !entryIds.has(milestone.splitAfterEntryId)) {
        ctx.addIssue({
          code: "custom",
          path: ["milestones", i, "splitAfterEntryId"],
          message: `unknown entry "${milestone.splitAfterEntryId}"`,
        });
      }
    });

    if (catalog.totals.entryCount !== catalog.entries.length) {
      ctx.addIssue({
        code: "custom",
        path: ["totals", "entryCount"],
        message: `entryCount ${catalog.totals.entryCount} != entries.length ${catalog.entries.length}`,
      });
    }
  });

/* ------------------------------------------------------------------------ *
 * Compile-time parity: the schemas above must infer EXACTLY the interfaces
 * in catalog.ts, in both directions. If either side drifts, tsc fails here.
 * ------------------------------------------------------------------------ */

type MutuallyAssignable<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
export type AssertTrue<T extends true> = T;

export type EntryParity = AssertTrue<MutuallyAssignable<z.output<typeof entrySchema>, Entry>>;
export type CatalogParity = AssertTrue<MutuallyAssignable<z.output<typeof catalogSchema>, Catalog>>;
